<template>
  <div :class="[$style.panel, { [$style.inline]: inline }]" test-id="sca-calendar-panel">
    <!-- Toolbar (calendar modes only) -->
    <div v-if="showCalendar" :class="$style.toolbar">
      <div :class="$style.toolbarControls">
        <SCAIconButton
          icon="phosphor/caret-left"
          size="small"
          type="default"
          appearance="secondary"
          :title="t('datetimePicker.previousMonth')"
          test-id="sca-calendar-prev"
          @click="shiftMonths(-1)"
        />
        <SCAIconButton
          icon="phosphor/caret-right"
          size="small"
          type="default"
          appearance="secondary"
          :title="t('datetimePicker.nextMonth')"
          test-id="sca-calendar-next"
          @click="shiftMonths(1)"
        />
        <SCADropdown
          v-if="cfg.maxMonths > 1"
          v-model:is-open="settingsOpen"
          :offset-y="5"
          placement="bottom-start"
          padding-upset
        >
          <template #trigger="{ togglePopover }">
            <SCAIconButton
              icon="phosphor/gear"
              size="small"
              type="default"
              appearance="secondary"
              :title="t('datetimePicker.settings')"
              test-id="sca-calendar-settings"
              @click="togglePopover()"
            />
          </template>
          <div :class="$style.settings">
            <SCASelect
              v-model="monthsToShow"
              :label="t('datetimePicker.months')"
              :items="monthOptions"
              test-id="sca-calendar-months-select"
            />
            <div :class="$style.columnsField">
              <SCAInput :label="t('datetimePicker.columns')">
                <div :class="$style.columnsRow">
                  <SCAButton
                    v-for="n in 4"
                    :key="n"
                    :class="$style.columnsButton"
                    size="small"
                    type="toggle"
                    :appearance="columnsCount === n ? 'primary' : 'secondary'"
                    padding-left="0px"
                    padding-right="0px"
                    :selected="columnsCount === n"
                    :disabled="n > monthsToShow"
                    :test-id="`sca-calendar-columns-${n}`"
                    @click="columnsCount = n"
                  >
                    {{ n }}
                  </SCAButton>
                </div>
              </SCAInput>
            </div>
          </div>
        </SCADropdown>
        <SCAButton
          size="small"
          type="default"
          appearance="secondary"
          padding-left="8px"
          padding-right="8px"
          @click="goToToday"
        >
          {{ t("datetimePicker.today") }}
        </SCAButton>
      </div>
      <div ref="toolbarContentRef" :class="$style.toolbarContent">
        <span :class="$style.rangeLabel">{{ visibleRangeLabel }}</span>
        <span ref="rangeProbeRef" :class="[$style.rangeLabel, $style.rangeProbe]" aria-hidden="true">{{
          fullRangeLabel
        }}</span>
        <span :class="$style.selectionLabel" test-id="sca-calendar-now">{{ nowLabel }}</span>
        <span ref="nowProbeRef" :class="[$style.selectionLabel, $style.rangeProbe]" aria-hidden="true">{{
          fullNowLabel
        }}</span>
      </div>
    </div>

    <!-- Months -->
    <div v-if="showCalendar" :class="[$style.months, { [$style.readonly]: readonly }]" :style="monthsGridStyle">
      <CalendarMonthGrid
        v-for="(month, i) in visibleMonths"
        :key="month.getTime()"
        :month="month"
        :week-starts-on="cfg.weekStartsOn"
        :show-week-numbers="cfg.showWeekNumbers"
        :weekdays="weekdays"
        :week-label="t('datetimePicker.weekShort')"
        :month-label="monthLabel(month)"
        :years="years"
        :year-selectable="true"
        :is-disabled="isDisabled"
        :get-day-state="getDayState"
        :readonly="readonly"
        :test-id="`sca-calendar-month-${i}`"
        @select="onSelectDay"
        @hover="onHoverDay"
        @select-year="onSelectYear"
        @mouseleave="hoverDate = null"
      />
    </div>

    <!-- Time area -->
    <div
      v-if="showTime"
      :class="[$style.timeArea, { [$style.noBorder]: !showCalendar, [$style.stacked]: mode === 'time' }]"
      test-id="sca-calendar-time"
    >
      <!-- Single time: separate Hour + Minutes (+ Seconds) dropdowns, stacked -->
      <template v-if="mode === 'time'">
        <div>
          <TimeUnitSelect
            ref="hoursSelect"
            :model-value="timeStart?.hours ?? null"
            :label="t('datetimePicker.hours')"
            unit="hours"
            test-id="sca-time-hours"
            @update:model-value="onUnitChange('hours', $event)"
            @next="minutesSelect?.focus()"
          />
          <TimeUnitSelect
            ref="minutesSelect"
            :model-value="timeStart?.minutes ?? null"
            :label="t('datetimePicker.minutes')"
            unit="minutes"
            :step="1"
            test-id="sca-time-minutes"
            @update:model-value="onUnitChange('minutes', $event)"
            @prev="hoursSelect?.focusLast()"
          />
        </div>
      </template>

      <!-- Single date + time: one combined hh:mm dropdown -->
      <template v-else-if="mode === 'datetime'">
        <div :class="$style.timeUnitRow">
          <TimeUnitSelect
            ref="hoursSelect"
            :model-value="timeStart?.hours ?? null"
            :label="t('datetimePicker.h')"
            unit="hours"
            test-id="sca-time-hours"
            :label-size="20"
            :width="85"
            :gap="4"
            @update:model-value="onUnitChange('hours', $event)"
            @next="minutesSelect?.focus()"
          />
          <TimeUnitSelect
            ref="minutesSelect"
            :model-value="timeStart?.minutes ?? null"
            :label="t('datetimePicker.m')"
            unit="minutes"
            :step="1"
            test-id="sca-time-minutes"
            :label-size="32"
            :width="105"
            :gap="4"
            @update:model-value="onUnitChange('minutes', $event)"
            @prev="hoursSelect?.focusLast()"
          />
        </div>
      </template>

      <!-- Range: Start + End combined dropdowns (+ All day) -->
      <template v-else>
        <TimeCombinedSelect
          :model-value="displayTimeStart"
          :label="t('datetimePicker.start')"
          :minute-step="cfg.minuteStep"
          :disabled="allDayModel"
          test-id="sca-time-start"
          :width="104"
          @update:model-value="onTimeChange('start', $event)"
        />
        <TimeCombinedSelect
          :model-value="displayTimeEnd"
          :label="t('datetimePicker.end')"
          :minute-step="cfg.minuteStep"
          :disabled="allDayModel"
          test-id="sca-time-end"
          :width="100"
          @update:model-value="onTimeChange('end', $event)"
        />
        <div :class="$style.allDayField">
          <span :class="$style.allDayLable">{{ t("datetimePicker.allDay") }}</span>
          <SCAToggle
            :model-value="allDayModel"
            :class="$style.allDayToggle"
            no-padding
            test-id="sca-time-allday"
            @update:model-value="onAllDayChange"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatWithLocale, longDateTimePattern, resolveDateFnsLocale, useNow } from "@scayla/core";
import { addMonths, endOfDay, format, isAfter, isBefore, isSameDay, startOfDay, startOfMonth } from "date-fns";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import SCADropdown from "../Dropdown/SCADropdown.vue";
import SCAButton from "../SCAButton.vue";
import SCAIconButton from "../SCAIconButton.vue";
import SCAInput from "../SCAInput.vue";
import SCASelect from "../SCASelect.vue";
import SCAToggle from "../SCAToggle.vue";
import CalendarMonthGrid from "./CalendarMonthGrid.vue";
import { weekdayLabels } from "./calendarUtils";
import type {
  CalendarDisplayConfig,
  DatetimePickerMode,
  DatetimePickerValue,
  DaySelectionState,
  RangeValue,
  TimeParts,
} from "./datetimePickerTypes";
import { CALENDAR_DISPLAY_CONFIG_DEFAULTS, hasDate, hasTime, isRangeMode } from "./datetimePickerTypes";
import TimeCombinedSelect from "./TimeCombinedSelect.vue";
import TimeUnitSelect from "./TimeUnitSelect.vue";

interface Props {
  mode: DatetimePickerMode;
  inline?: boolean;
  readonly?: boolean;
  config?: CalendarDisplayConfig;
}

const props = withDefaults(defineProps<Props>(), {
  inline: false,
  readonly: false,
  config: undefined,
});

// Read once at setup — the config is set-once by design, not reactive.
const cfg = { ...CALENDAR_DISPLAY_CONFIG_DEFAULTS, ...props.config };

const emit = defineEmits<{
  selectionComplete: [];
}>();

const model = defineModel<DatetimePickerValue>({ required: true });

const { t, locale } = useI18n();
const dateFnsLocale = computed(() => resolveDateFnsLocale(locale.value));

const isRange = computed(() => isRangeMode(props.mode));
const showCalendar = computed(() => hasDate(props.mode));
const showTime = computed(() => hasTime(props.mode));

// When "all day" is on the emitted range spans start-of-day..end-of-day, so the
// (disabled) time selects should reflect those fixed bounds instead of stale values.
const ALL_DAY_START: TimeParts = { hours: 0, minutes: 0, seconds: 0 };
const ALL_DAY_END: TimeParts = { hours: 23, minutes: 59, seconds: 59 };

// ---- Internal selection state ----
const selDate = ref<Date | null>(null); // single-date modes
const rngStart = ref<Date | null>(null);
const rngEnd = ref<Date | null>(null);
const timeStart = ref<TimeParts | null>(null);
const timeEnd = ref<TimeParts | null>(null);
const hoverDate = ref<Date | null>(null);

const hoursSelect = ref<InstanceType<typeof TimeUnitSelect>>();
const minutesSelect = ref<InstanceType<typeof TimeUnitSelect>>();

const allDayModel = defineModel<boolean>("allDay", { default: false });

const displayTimeStart = computed(() => (allDayModel.value ? ALL_DAY_START : timeStart.value));
const displayTimeEnd = computed(() => (allDayModel.value ? ALL_DAY_END : timeEnd.value));

function onAllDayChange(v?: boolean | null) {
  if (props.readonly) return;
  const next = v ?? false;
  allDayModel.value = next;
  emitValue(next);
}

// ---- View state ----
// months / columns are two-way models — the gear-popover settings are persisted
// per user, so changes must reach the parent.
const baseMonth = ref<Date>(startOfMonth(new Date()));
const monthsToShow = defineModel<number>("months", { default: 1 });
const columnsCount = defineModel<number>("columns", { default: 1 });
const settingsOpen = ref(false);

watch(monthsToShow, (count) => {
  if (columnsCount.value > count) columnsCount.value = count;
});

const visibleMonths = computed<Date[]>(() =>
  Array.from({ length: Math.min(monthsToShow.value, cfg.maxMonths) }, (_, i) => addMonths(baseMonth.value, i)),
);

const monthsGridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${columnsCount.value}, max-content)`,
}));

const weekdays = computed(() => weekdayLabels(cfg.weekStartsOn, dateFnsLocale.value));

const YEARS_PAST = 100;
const YEARS_FUTURE = 100;
const years = computed<number[]>(() => {
  const thisYear = new Date().getFullYear();
  const viewed = baseMonth.value.getFullYear();
  const from = cfg.min ? cfg.min.getFullYear() : Math.min(thisYear - YEARS_PAST, viewed);
  const to = cfg.max ? cfg.max.getFullYear() : Math.max(thisYear + YEARS_FUTURE, viewed);
  const out: number[] = [];
  for (let y = from; y <= to; y++) out.push(y);
  return out;
});

const monthOptions = computed(() =>
  Array.from({ length: cfg.maxMonths }, (_, i) => ({ id: i + 1, label: String(i + 1) })),
);

// ---- Labels ----
// `LLLL` = full standalone month ("September"), `LLL` = abbreviated ("Sep") — the
// short form is used only when the full range label would overflow the toolbar.
function monthLabel(month: Date, short = false): string {
  return format(month, short ? "LLL yyyy" : "LLLL yyyy", { locale: dateFnsLocale.value });
}

function rangeLabelFor(short: boolean): string {
  const list = visibleMonths.value;
  if (list.length <= 1) return monthLabel(list[0], short);
  return `${monthLabel(list[0], short)} - ${monthLabel(list[list.length - 1], short)}`;
}

// The visible label switches to the abbreviated month name when the full-format
// probe (rendered off-screen at full width) doesn't fit the available space.
const useShortMonth = ref(false);
const fullRangeLabel = computed(() => rangeLabelFor(false));
const visibleRangeLabel = computed(() => (useShortMonth.value ? rangeLabelFor(true) : fullRangeLabel.value));

const now = useNow();
// Falls back to a compact numeric date when the full one would overflow the toolbar.
function nowLabelFor(short: boolean): string {
  if (!now.value) return "";
  return formatWithLocale(now.value, short ? "dd.MM.yyyy, HH:mm" : longDateTimePattern(locale.value), {
    locale: locale.value,
  });
}

const useShortNow = ref(false);
const fullNowLabel = computed(() => nowLabelFor(false));
const nowLabel = computed(() => (useShortNow.value ? nowLabelFor(true) : fullNowLabel.value));

const toolbarContentRef = ref<HTMLElement | null>(null);
const rangeProbeRef = ref<HTMLElement | null>(null);
const nowProbeRef = ref<HTMLElement | null>(null);
let rangeResizeObserver: ResizeObserver | null = null;

const RANGE_LABEL_SAFETY_PX = 4;

function fitsProbe(probe: HTMLElement | null, available: number): boolean {
  if (!probe) return true;
  return probe.getBoundingClientRect().width <= available - RANGE_LABEL_SAFETY_PX;
}

function measureLabels() {
  const host = toolbarContentRef.value;
  if (!host) return;
  const available = host.getBoundingClientRect().width;
  useShortMonth.value = !fitsProbe(rangeProbeRef.value, available);
  useShortNow.value = !fitsProbe(nowProbeRef.value, available);
}

onMounted(() => {
  if (typeof ResizeObserver !== "undefined" && toolbarContentRef.value) {
    rangeResizeObserver = new ResizeObserver(() => measureLabels());
    rangeResizeObserver.observe(toolbarContentRef.value);
  }
  void nextTick(measureLabels);
});

onBeforeUnmount(() => {
  rangeResizeObserver?.disconnect();
  rangeResizeObserver = null;
});

// The probe text changes (navigation, range span, locale, ticking clock) without the
// available width changing, so ResizeObserver won't fire — re-measure after the DOM updates.
watch([fullRangeLabel, fullNowLabel], () => void nextTick(measureLabels));

// ---- Disabled predicate ----
function isDisabled(date: Date): boolean {
  if (cfg.min && isBefore(startOfDay(date), startOfDay(cfg.min))) return true;
  if (cfg.max && isAfter(startOfDay(date), startOfDay(cfg.max))) return true;
  return cfg.disabledDate ? cfg.disabledDate(date) : false;
}

// ---- Day selection state for grids ----
function getDayState(date: Date): DaySelectionState {
  const empty: DaySelectionState = {
    selected: false,
    rangeStart: false,
    rangeEnd: false,
    inRange: false,
    preview: false,
    previewStart: false,
    previewEnd: false,
    flash: isSameDay(date, new Date()) ? flashTick.value : 0,
  };
  if (!isRange.value) {
    return { ...empty, selected: selDate.value ? isSameDay(date, selDate.value) : false };
  }

  const start = rngStart.value;
  const end = rngEnd.value;
  const state = { ...empty };

  if (start && isSameDay(date, start)) state.rangeStart = true;
  if (end && isSameDay(date, end)) state.rangeEnd = true;
  if (start && end && isAfter(startOfDay(date), startOfDay(start)) && isBefore(startOfDay(date), startOfDay(end))) {
    state.inRange = true;
  }

  // Hover preview while picking the end of the range
  if (start && !end && hoverDate.value && !isDisabled(hoverDate.value)) {
    const hover = hoverDate.value;
    const [a, b] = orderDates(start, hover);
    const d = startOfDay(date);
    const backward = isBefore(startOfDay(hover), startOfDay(start));
    // Set on every cell (not just preview ones) so cells leaving the preview retract toward the anchor too.
    state.previewDirection = backward ? "backward" : "forward";
    if (isAfter(d, startOfDay(a)) && isBefore(d, startOfDay(b))) state.preview = true;
    if (!isSameDay(hover, start)) {
      // Hovering backwards makes the selected anchor the range's right endpoint,
      // so its connecting bar must point left instead of right.
      if (isSameDay(date, start) && backward) {
        state.rangeStart = false;
        state.rangeEnd = true;
      }
      if (isSameDay(date, hover)) {
        state.preview = true;
        if (backward) state.previewStart = true;
        else state.previewEnd = true;
      }
    }
  }
  return state;
}

function orderDates(a: Date, b: Date): [Date, Date] {
  return isAfter(a, b) ? [b, a] : [a, b];
}

// ---- Navigation ----
function shiftMonths(delta: number) {
  baseMonth.value = addMonths(baseMonth.value, delta);
}

function goToToday() {
  const now = new Date();
  const today = startOfDay(now);
  baseMonth.value = startOfMonth(today);
  flashTick.value++;

  if (props.readonly || isDisabled(today)) return;

  const currentTimeParts: TimeParts = { hours: now.getHours(), minutes: now.getMinutes(), seconds: now.getSeconds() };

  if (isRange.value) {
    rngStart.value = today;
    rngEnd.value = today;
    hoverDate.value = null;
    if (hasTime(props.mode)) {
      timeStart.value = { ...currentTimeParts };
      timeEnd.value = { ...currentTimeParts };
    }
    emitValue();
    if (props.mode === "dateRange") emit("selectionComplete");
  } else {
    selDate.value = today;
    if (hasTime(props.mode)) timeStart.value = { ...currentTimeParts };
    emitValue();
    if (props.mode === "date") emit("selectionComplete");
  }
}

const flashTick = ref(0);
function onSelectYear(year: number) {
  baseMonth.value = new Date(year, baseMonth.value.getMonth(), 1);
}
function onHoverDay(date: Date) {
  if (isRange.value && rngStart.value && !rngEnd.value) hoverDate.value = date;
}

// ---- Selection handlers ----
function onSelectDay(date: Date) {
  if (props.readonly) return;
  if (isDisabled(date)) return;
  const day = startOfDay(date);
  const now = new Date();
  const currentTimeParts: TimeParts = { hours: now.getHours(), minutes: now.getMinutes(), seconds: now.getSeconds() };

  if (!isRange.value) {
    selDate.value = day;
    if (hasTime(props.mode)) timeStart.value = { ...currentTimeParts };
    emitValue();
    if (props.mode === "date") emit("selectionComplete");
    return;
  }

  // Range selection state machine
  if (!rngStart.value || (rngStart.value && rngEnd.value)) {
    rngStart.value = day;
    rngEnd.value = null;
    hoverDate.value = null;
  } else {
    const [a, b] = orderDates(rngStart.value, day);
    rngStart.value = a;
    rngEnd.value = b;
    hoverDate.value = null;
    if (hasTime(props.mode)) {
      timeStart.value = { ...currentTimeParts };
      timeEnd.value = { ...currentTimeParts };
    }
  }
  emitValue();
  if (props.mode === "dateRange" && rngStart.value && rngEnd.value) emit("selectionComplete");
}

function onTimeChange(which: "start" | "end", parts: TimeParts) {
  if (which === "start") timeStart.value = parts;
  else timeEnd.value = parts;
  emitValue();
}

function onUnitChange(unit: keyof TimeParts, value: number) {
  const next: TimeParts = timeStart.value ? { ...timeStart.value } : { hours: 0, minutes: 0, seconds: 0 };
  next[unit] = value;
  timeStart.value = next;
  emitValue();
}

// ---- Value composition ----
function combine(date: Date, time: TimeParts | null): Date {
  const result = new Date(date);
  result.setHours(time?.hours ?? 0, time?.minutes ?? 0, time?.seconds ?? 0, 0);
  return result;
}

function composeSingleValue(): Date | null {
  if (props.mode === "date") return selDate.value;
  if (props.mode === "datetime") return selDate.value ? combine(selDate.value, timeStart.value) : null;
  if (props.mode === "time") return timeStart.value ? combine(startOfDay(new Date()), timeStart.value) : null;
  return null;
}

function composeRangeValue(allDay: boolean): RangeValue {
  if (props.mode === "dateRange") return [rngStart.value, rngEnd.value];
  if (props.mode === "timeRange") {
    const base = startOfDay(new Date());
    if (allDay) return [startOfDay(base), endOfDay(base)];
    return [
      timeStart.value ? combine(base, timeStart.value) : null,
      timeEnd.value ? combine(base, timeEnd.value) : null,
    ];
  }
  // datetimeRange
  if (allDay) {
    return [rngStart.value ? startOfDay(rngStart.value) : null, rngEnd.value ? endOfDay(rngEnd.value) : null];
  }
  return [
    rngStart.value ? combine(rngStart.value, timeStart.value) : null,
    rngEnd.value ? combine(rngEnd.value, timeEnd.value) : null,
  ];
}

let internalUpdate = false;

function emitValue(allDayOverride?: boolean) {
  const value: DatetimePickerValue = isRange.value
    ? composeRangeValue(allDayOverride ?? allDayModel.value)
    : composeSingleValue();
  internalUpdate = true;
  model.value = value;
}

// ---- Sync from external model ----
function syncFromModel() {
  // Skip the echo of our own emit so we don't fight the parent / reset the view.
  if (internalUpdate) {
    internalUpdate = false;
    return;
  }
  if (isRange.value) {
    const [s, e] = (model.value as RangeValue | null) ?? [null, null];
    rngStart.value = s ? startOfDay(s) : null;
    rngEnd.value = e ? startOfDay(e) : null;
    timeStart.value = s ? toParts(s) : timeStart.value;
    timeEnd.value = e ? toParts(e) : timeEnd.value;
    if (s) baseMonth.value = startOfMonth(s);
  } else {
    const v = model.value as Date | null;
    selDate.value = v ? startOfDay(v) : null;
    if (v && hasTime(props.mode)) timeStart.value = toParts(v);
    if (v && hasDate(props.mode)) baseMonth.value = startOfMonth(v);
  }
}

function toParts(d: Date): TimeParts {
  return { hours: d.getHours(), minutes: d.getMinutes(), seconds: d.getSeconds() };
}

watch(model, syncFromModel, { immediate: true });

defineExpose({ goToToday });
</script>

<style module lang="scss">
@use "../../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;

.panel {
  display: flex;
  flex-direction: column;
  background-color: var(--color-interaction-background-modeless, #fff);
  // NB: no `overflow: hidden` here — it would create a sticky containing block
  // that traps the sticky toolbar inside the (non-scrolling) panel. The scroll
  // container is the popover, so the toolbar must be free to stick against it.
}

.inline {
  width: max-content;
}

.toolbar {
  position: sticky;
  top: 0;
  // Above the sticky per-month period headers (z-index 2) and the day-cell circles
  // (position:relative; z-index:1), which would otherwise paint over the toolbar
  // as they scroll under it.
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 13px;
  min-height: 44px;
  padding: var(--space-component-padding-xSmall) var(--space-component-padding-medium);
  border-bottom: 1px solid var(--color-border-default, #eaedf0);
  background-color: var(--color-interaction-background-modeless, #fff);
  width: 0;
  min-width: 100%;
  box-sizing: border-box;
}

.toolbarControls {
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-small, 8px);
}

.toolbarContent {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.rangeLabel {
  @include FigmaTypoMixins.typo-body-strong-medium;
  color: var(--color-content-primary, #272e35);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 14px;
}

.selectionLabel {
  @include FigmaTypoMixins.typo-body-xsmall;
  color: var(--color-content-tertiary, #6b7682);
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 12px;
}

.rangeProbe {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  overflow: visible;
  max-width: none;
}

.months {
  display: grid;
  row-gap: var(--space-component-padding-medium);
  justify-content: center;
}

.settings {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 200px;
  padding: var(--space-component-padding-medium);
  --sca-label-size: 52px;
}

.columnsField {
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    letter-spacing: 0.24px;
  }
}

.columnsLabel {
  @include FigmaTypoMixins.typo-body-medium;
  color: var(--color-content-primary, #272e35);
}

.columnsRow {
  display: flex;
  gap: 4px;
}

.columnsButton {
  width: 24px;
  min-width: 24px;
  height: 24px;
  line-height: 24px;
}

.timeArea {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-component-gap-small);
  min-height: 44px;
  padding: var(--space-component-padding-medium, 12px);
  border-top: 1px solid var(--color-border-default, #eaedf0);
}

.timeArea:not(.noBorder) {
  width: 0;
  min-width: 100%;
  box-sizing: border-box;
}

.noBorder {
  border-top: none;
}

.stacked {
  flex-direction: column;
  align-items: stretch;
  flex-wrap: nowrap;
  gap: var(--space-component-gap-small, 8px);
}

.timeUnitRow {
  display: flex;
  align-items: center;
  gap: 10px;
}

.allDayField {
  display: flex;
  gap: var(--space-component-padding-xSmall);
}

.allDayLable {
  @include FigmaTypoMixins.typo-body-medium;
  color: var(--color-content-disabled, #8896a6);
  letter-spacing: 0.24px;
  white-space: nowrap;
  width: 52px;
}

.allDayToggle {
  margin-left: auto;
}

@media (max-width: 768px) {
  .inline {
    width: 100%;
  }
  .months {
    grid-template-columns: 1fr !important;
    justify-content: stretch;
  }
}
</style>
